/**
 * ContentComponentFullScreenSupport
 *
 * Complete content display component that combines VideoComponent with
 * user-generated content UI elements (user info, description, actions, links).
 *
 * Supports CSS-based fullscreen:
 * - Video stays in place (no DOM re-parenting)
 * - position: fixed used to escape container constraints
 * - Placeholder maintains list space when fullscreen
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/presentation/store';
import { VideoComponent } from '@/presentation/components/video/VideoComponent';
import { ContentComponentOverlays } from '@/presentation/components/video/ContentComponentOverlays';
import { VideoPlayblackCurrentProgress } from '@/presentation/components/video/VideoPlayblackCurrentProgress';
import { VideoProgressBar } from '@/presentation/components/video/VideoProgressBar';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { UserInfoComponent } from './UserInfoComponent';
import { ExpandableRichText } from '@/presentation/components/common/text/ExpandableRichText';
import { ContentActions } from './ContentActions';
import { ContentLinksComponent } from './ContentLinksComponent';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { GiftEntity } from '@/domain/entities/gift/giftEntity';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useIsLandscape, useResponsiveDesign, useViewportDimensions } from '@/presentation/responsiveDesign/responsiveDesign';
import { useUserMediaLike } from '@/presentation/feature/content/hooks/useUserMediaLike';
import { useUnlockContent } from '@/presentation/feature/content/hooks/useUnlockContent';
import { useContentPlaybackTracker } from '@/presentation/feature/content/hooks/useContentPlaybackTracker';
import { useExplicitContentGate } from '@/presentation/feature/content/hooks/useExplicitContentGate';
import { setFullscreenContainer } from '@/presentation/store/slices/videoManagement/videoManagementSlice';
import { useVideoManagement } from '@/presentation/feature/content/hooks/useVideoManagement';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';
import { GiftUtils } from '@/domain/utils/giftUtils';

export interface ContentComponentFullScreenSupportProps {
  /** Screen key that will be used for video management and identification */
  screenKey: string;

  /** Media entity containing video information */
  media: UserMediaEntity;

  /** Whether to autoplay after initialization */
  autoplay?: boolean;

  /** Whether to mute video */
  muted?: boolean;

  /** Custom CSS class */
  className?: string;

  /** Whether video is currently in fullscreen mode */
  isFullscreen?: boolean;

  /** Current user ID */
  currentUserId?: string;

  /** Whether user is subscribed to content creator */
  subscribed?: boolean;

  /** Whether to show the more button in user info */
  showMore?: boolean;

  /** Video interaction callbacks */
  onClick?: (media: UserMediaEntity) => void;
  onInitStart?: (media: UserMediaEntity) => void;
  onReady?: (media: UserMediaEntity) => void;
  onPlaybackStart?: (media: UserMediaEntity) => void;
  onFullscreenClick?: (media: UserMediaEntity) => void;
  onFullscreenClose?: () => void;
  onVideoElementReady?: (media: UserMediaEntity, videoElement: HTMLVideoElement) => void;
  onVideoFullScreenStateChange?: (media: UserMediaEntity, state: {
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    volume: number;
    isMuted: boolean;
  }) => void;
  onError?: (media: UserMediaEntity, error: Error) => void;

  /** Content UI interaction callbacks */
  onUserTap?: (userId: string) => Promise<void> | void;
  onMoreTap?: (userId: string) => Promise<void> | void;
  onHashtagTap?: (hashtag: string) => Promise<void> | void;
  onMentionTap?: (mention: string) => Promise<void> | void;
  onUrlTap?: (url: string) => Promise<void> | void;
  onLikeTap?: () => Promise<void> | void;
  onCommentTap?: () => Promise<void> | void;
  onGiftTap?: (defaultGift?: GiftEntity) => Promise<void> | void;
  onShareTap?: () => Promise<void> | void;
  onContentLinkTap?: () => Promise<void> | void;
  onCommentReplyTap?: () => Promise<void> | void;
  onScheduleTap?: () => Promise<void> | void;
  onDraftTap?: () => Promise<void> | void;
  onGet4kAccessTap?: () => Promise<void> | void;
  onNsfwTap?: (media: UserMediaEntity) => Promise<void> | void;
  onUnlockContentTap?: () => Promise<void> | void;
  onContentBlockedTap?: () => Promise<void> | void;

  /** QA tag for the row root (`data-qa-id`); a `data-qa-status` lock token is merged in. */
  qa?: QaAttrOptions;
}

export const ContentComponentFullScreenSupport: React.FC<ContentComponentFullScreenSupportProps> = ({
  screenKey,
  media,
  autoplay = false,
  muted = false,
  className = '',
  isFullscreen = false,
  currentUserId,
  subscribed = false,
  showMore = false,
  onClick,
  onInitStart,
  onReady,
  onPlaybackStart,
  onFullscreenClick,
  onFullscreenClose,
  onVideoElementReady,
  onVideoFullScreenStateChange,
  onError,
  onUserTap,
  onMoreTap,
  onHashtagTap,
  onMentionTap,
  onUrlTap,
  onLikeTap,
  onCommentTap,
  onGiftTap,
  onShareTap,
  onContentLinkTap,
  onCommentReplyTap,
  onScheduleTap,
  onDraftTap,
  onGet4kAccessTap,
  onNsfwTap,
  onUnlockContentTap,
  onContentBlockedTap,
  qa,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const viewport = useViewportDimensions();
  const isLandscape = useIsLandscape();

  // Video management - unified playback state for both normal and fullscreen modes
  const videoKey = MediaUtils.composeVideoPlayerKey(screenKey, media.id);
  const duration = media.duration ? media.duration / 1000 : 0;
  const typeLimit = media.typeLimit ? media.typeLimit / 1000 : null;
  const isOwner = !!currentUserId && currentUserId === media.firebaseId;
  const hasContentOrHashTags = (media.contentTags?.length ?? 0) > 0 || (media.hashTags?.length ?? 0) > 0;
  const {
    playbackStates,
    togglePlayPauseActiveVideo,
    seekActiveVideo,
  } = useVideoManagement();
  const playbackState = playbackStates[videoKey];

  // Watch like state from SecureStorage via hook pattern
  const { likeData } = useUserMediaLike(media.relativePath);

  const { hasExclusiveRights } = useUnlockContent(media);

  // CSS-based fullscreen: track original dimensions for placeholder
  const contentRef = useRef<HTMLDivElement>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const [originalDimensions, setOriginalDimensions] = useState<{ width: number; height: number } | null>(null);

  // Derive isInitialized from Redux (same logic as ContentComponentOverlays)
  const videoState = useSelector((state: RootState) => state.video.videos[videoKey]);
  const isInitialized = !!(
    videoState &&
    !videoState.isLoading &&
    !videoState.error &&
    !videoState.isDisposed &&
    videoState.initializedAt > 0
  );

  const [isVideoReady, setIsVideoReady] = useState(false);

  const { typeLimitReached, defaultGift } = useContentPlaybackTracker({
    videoKey,
    typeLimitMs: media.typeLimit,
    hasExclusiveRights,
    isVideoReady,
    contentId: media.id,
    isContent: media.isContent,
    isOwner,
    hasContentOrHashTags,
    durationMs: media.duration ?? 0,
    ownerUserId: media.firebaseId,
    currentViews: media.views,
  });

  const { gated: nsfwGated, reveal: revealNsfw } = useExplicitContentGate({
    videoKey,
    media,
    isOwner,
    isNsfw: MediaUtils.isNsfwContent(media.explicit),
  });

  const handleGiftTap = useCallback(() => {
    return onGiftTap?.(defaultGift);
  }, [onGiftTap, defaultGift]);

  // Fullscreen controls visibility
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollLockAppliedRef = useRef(false);

  // Capture dimensions when entering fullscreen to maintain placeholder space
  useEffect(() => {
    if (isFullscreen && videoWrapperRef.current && !originalDimensions) {
      const rect = videoWrapperRef.current.getBoundingClientRect();
      setOriginalDimensions({ width: rect.width, height: rect.height });
    } else if (!isFullscreen && originalDimensions) {
      setOriginalDimensions(null);
    }
  }, [isFullscreen, originalDimensions]);

  // Scroll locking when fullscreen - uses ref to track if we applied the lock
  useEffect(() => {
    const restoreScroll = () => {
      if (scrollLockAppliedRef.current) {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        document.documentElement.style.overscrollBehavior = '';
        document.body.style.overscrollBehavior = '';
        // Restore scrollbar-gutter (index.css sets it to 'stable')
        document.documentElement.style.scrollbarGutter = '';
        document.body.style.scrollbarGutter = '';
        scrollLockAppliedRef.current = false;
      }
    };

    if (!isFullscreen) {
      // Restore scroll when exiting fullscreen
      restoreScroll();
      return;
    }

    // Apply scroll lock when entering fullscreen
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'contain';
    document.body.style.overscrollBehavior = 'contain';
    // Remove scrollbar gutter reservation (index.css sets scrollbar-gutter: stable)
    // This allows fullscreen content to use the full viewport width
    document.documentElement.style.scrollbarGutter = 'auto';
    document.body.style.scrollbarGutter = 'auto';
    scrollLockAppliedRef.current = true;

    // Cleanup on unmount
    return restoreScroll;
  }, [isFullscreen]);

  // Store onFullscreenClose in ref to avoid stale closures in event handlers
  const onFullscreenCloseRef = useRef(onFullscreenClose);
  useEffect(() => {
    onFullscreenCloseRef.current = onFullscreenClose;
  }, [onFullscreenClose]);

  // Keyboard shortcuts when fullscreen
  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onFullscreenCloseRef.current?.();
      } else if (e.code === 'Space') {
        e.preventDefault();
        togglePlayPauseActiveVideo();
      } else if (e.code === 'KeyF') {
        e.preventDefault();
        onFullscreenCloseRef.current?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, togglePlayPauseActiveVideo]);

  // Handle browser back button - push dummy state so back closes fullscreen
  useEffect(() => {
    if (!isFullscreen) return;

    // Push a state to history when opening fullscreen
    window.history.pushState({ fullscreen: true }, '');

    const handlePopState = () => {
      // If user clicks back, close fullscreen instead of navigating away
      onFullscreenCloseRef.current?.();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Remove the state we pushed if still in fullscreen when unmounting
      if (window.history.state?.fullscreen) {
        window.history.back();
      }
    };
  }, [isFullscreen]);

  // Handle native fullscreen changes (e.g., user presses ESC or exits via browser button)
  useEffect(() => {
    if (!isFullscreen) return;

    // Track if we were in browser fullscreen
    let wasInBrowserFullscreen = !!(document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement);

    const handleFullscreenChange = () => {
      const isInFullscreen = !!(document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement);

      // Only close if:
      // 1. We WERE in browser fullscreen
      // 2. And now we're NOT (user pressed ESC or exited)
      if (wasInBrowserFullscreen && !isInFullscreen && isFullscreen) {
        onFullscreenCloseRef.current?.();
      }

      // Update tracking variable
      wasInBrowserFullscreen = isInFullscreen;
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [isFullscreen]);

  // Auto-hide controls when playing
  const resetHideTimeout = useCallback(() => {
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }

    if (playbackState?.isPlaying) {
      hideControlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2000);
    }
  }, [playbackState?.isPlaying]);

  useEffect(() => {
    if (!isFullscreen) return;

    if (!playbackState?.isPlaying) {
      setShowControls(true);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    } else {
      setShowControls(true);
      resetHideTimeout();
    }
  }, [isFullscreen, playbackState?.isPlaying, resetHideTimeout]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, []);

  // Register fullscreen container with Redux for browser Fullscreen API
  useEffect(() => {
    if (isFullscreen && videoWrapperRef.current) {
      setFullscreenContainer(videoWrapperRef.current);
    }
    return () => {
      if (isFullscreen) {
        setFullscreenContainer(null);
      }
    };
  }, [isFullscreen]);

  const handleFullscreenOverlayClick = useCallback(() => {
    if (playbackState?.isPlaying) {
      setShowControls(true);
      resetHideTimeout();
    }
    togglePlayPauseActiveVideo();
  }, [playbackState?.isPlaying, resetHideTimeout, togglePlayPauseActiveVideo]);

  const handleFullscreenToggle = useCallback(() => {
    if (isFullscreen) {
      onFullscreenClose?.();
    } else {
      onFullscreenClick?.(media);
    }
  }, [isFullscreen, onFullscreenClose, onFullscreenClick, media]);

  // Video wrapper styles - changes between normal and fullscreen
  const videoWrapperStyle: React.CSSProperties = isFullscreen ? {
    position: 'fixed',
    top: 0,
    left: 0,
    width: viewport.width,
    height: viewport.height,
    zIndex: 9999,
    backgroundColor: theme.black,
    touchAction: 'none',
    cursor: showControls ? 'pointer' : 'none',
    overflow: 'hidden',
  } : {};

  // Placeholder style when fullscreen - maintains space in list
  const placeholderStyle: React.CSSProperties = isFullscreen && originalDimensions ? {
    width: originalDimensions.width,
    height: originalDimensions.height,
  } : {};

  // Lock token for the row root, derived from already-computed unlock state.
  // Only meaningful when the media is gated behind an unlock charge.
  const isUnlockable = !!media.allowUnlock && (media.unlockCharge ?? 0) > 0;
  const lockStatus = isUnlockable
    ? (hasExclusiveRights ? 'unlocked' : 'locked')
    : undefined;
  const rootQa = qa ? { ...qa, status: lockStatus } : undefined;

  // Playback token for the media surface, reflecting the live playback state.
  const playbackStatus = playbackState?.isPlaying ? 'playing' : 'paused';

  return (
    <div
      ref={contentRef}
      {...(rootQa ? qaAttr(rootQa) : {})}
      className={`flex flex-col w-full ${className}`}
    >
      {/* List mode: user info, description, links above video */}
      {!isFullscreen && media.firebaseId && (
        <>
          <div className="px-4">
            <UserInfoComponent
              userId={media.firebaseId}
              contentId={media.id}
              contentCreatedTimestamp={media.createdTimestamp}
              views={media.views}
              showProfileImage={true}
              showDate={false}
              userName={media.userName || '--'}
              subscribed={subscribed}
              currentUserId={currentUserId}
              showMore={showMore}
              onUserTap={onUserTap || (async () => { })}
              onMoreTap={onMoreTap}
            />
          </div>

          {media.description && (
            <div className="px-4 pt-4">
              <ExpandableRichText
                description={media.description}
                maxLines={3}
                onHashtagTap={onHashtagTap}
                onMentionTap={onMentionTap}
                onUrlTap={onUrlTap}
              />
            </div>
          )}

          <div className="px-4 pt-4">
            <ContentLinksComponent
              media={media}
              showShare={currentUserId === media.firebaseId}
              isNsfw={MediaUtils.isNsfwContent(media.explicit)}
              isPremiumVideo={MediaUtils.isPremiumVideo(media.duration)}
              is4k={MediaUtils.is4kVideo(media.resolution)}
              currentUserId={currentUserId}
              has4kAccess={false}
              onContentLinkTap={onContentLinkTap}
              onCommentReplyTap={onCommentReplyTap}
              onShareTap={onShareTap}
              onScheduleTap={onScheduleTap}
              onDraftTap={onDraftTap}
              onGet4kAccessTap={onGet4kAccessTap}
              onNsfwTap={onNsfwTap}
              onContentBlockedTap={onContentBlockedTap}
            />
          </div>

          {/* Non full screen */}
          <div className="px-4 pt-4">
            <ContentActions
              contentId={media.id}
              userId={media.firebaseId || ''}
              subscribed={subscribed}
              likes={media.likes}
              comments={media.comments}
              coins={media.coins}
              displayAddComment={true}
              showCoins={media.showCoins}
              defaultGiftUrl={defaultGift ? GiftUtils.getGiftPath(defaultGift.image) : undefined}
              isLiked={likeData?.liked || false}
              onUserTap={onUserTap || (async () => { })}
              onAddCommentTap={onCommentTap || (async () => { })}
              onLikeTap={onLikeTap || (async () => { })}
              onSendGiftTap={handleGiftTap}
              onMoreTap={onMoreTap ? () => onMoreTap(media.firebaseId || '') : undefined}
              onShareSocialContentTap={onShareTap || (async () => { })}
            />
          </div>

          <div className="pt-8" />
        </>
      )}

      {/* Placeholder maintains space in list when fullscreen */}
      <div style={placeholderStyle}>
        {/* Video wrapper - same element in both modes; goes position:fixed in fullscreen.
            In fullscreen it is also the click-to-toggle play/pause surface. */}
        <div
          ref={videoWrapperRef}
          {...qaAttr({
            id: isFullscreen ? `fullscreen-playpause-${media.id}` : `content-playpause-${media.id}`,
            status: playbackStatus,
          })}
          className={`relative ${!isFullscreen && isLandscape ? 'rounded-lg overflow-hidden' : ''}`}
          style={videoWrapperStyle}
          onClick={isFullscreen ? handleFullscreenOverlayClick : undefined}
          onMouseMove={isFullscreen ? () => {
            if (playbackState?.isPlaying) {
              setShowControls(true);
              resetHideTimeout();
            }
          } : undefined}
        >
          {/* Video */}
          <div
            style={isFullscreen ? {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.black,
            } : {}}
          >
            <VideoComponent
              screenKey={screenKey}
              media={media}
              autoplay={autoplay}
              muted={muted}
              isFullscreen={isFullscreen}
              onClick={onClick}
              onInitStart={onInitStart}
              onReady={onReady}
              onPlaybackStart={(m) => { setIsVideoReady(true); onPlaybackStart?.(m); }}
              onFullscreenClick={isFullscreen ? (onFullscreenClose ? () => onFullscreenClose() : undefined) : onFullscreenClick}
              onVideoElementReady={onVideoElementReady}
              onVideoFullScreenStateChange={onVideoFullScreenStateChange}
              onError={onError}
            />
          </div>

          {/* List mode overlays on video */}
          {!isFullscreen && (
            <>
              {/* Full-size overlay: unlock info + playback buttons + saved info.
                  Sits above the preview thumbnail (z-30) so it's visible even
                  before the video has been initialized. The overlay's
                  backdrop is click-through. */}
              <div className="absolute inset-0 z-40 pointer-events-none">
                <ContentComponentOverlays
                  screenKey={screenKey}
                  media={media}
                  playbackState={playbackState}
                  duration={duration}
                  isVideoReady={isVideoReady}
                  autoHide={true}
                  onFullscreenClick={() => handleFullscreenToggle()}
                  hasExclusiveRights={hasExclusiveRights}
                  typeLimitReached={typeLimitReached}
                  onUnlockContentTap={onUnlockContentTap}
                  nsfwGated={nsfwGated}
                  onNsfwReveal={revealNsfw}
                />
              </div>

              {/* Progress bar */}
              <div className="absolute bottom-0 left-4 right-4 z-40">
                <VideoProgressBar
                  videoKey={videoKey}
                  duration={duration}
                  typeLimit={typeLimit}
                  onSeek={seekActiveVideo}
                  isVideoReady={isVideoReady}
                />
              </div>
            </>
          )}

          {/* Fullscreen overlays */}
          {isFullscreen && (
            <>
              {/* Top overlay - back button + user info, description, links */}
              {media.firebaseId && (
                <div
                  className={`absolute top-0 left-0 right-0 z-40 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                  style={{
                    background: `linear-gradient(to bottom, ${theme.black}CC 0%, ${theme.black}00 100%)`,
                    paddingTop: `calc(${responsive.safeArea.top} + ${responsive.spacing.base}px)`,
                    paddingLeft: responsive.safeArea.left,
                    paddingRight: responsive.safeArea.right,
                    touchAction: 'auto',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >

                  <div className="px-4">
                    <UserInfoComponent
                      userId={media.firebaseId}
                      contentId={media.id}
                      contentCreatedTimestamp={media.createdTimestamp}
                      views={media.views}
                      showProfileImage={true}
                      showDate={false}
                      userName={media.userName || '--'}
                      subscribed={subscribed}
                      currentUserId={currentUserId}
                      showMore={showMore}
                      onUserTap={onUserTap || (async () => { })}
                      onMoreTap={onMoreTap}
                    />
                  </div>

                  <div className="px-4 pt-4">
                    <ContentLinksComponent
                      media={media}
                      showShare={currentUserId === media.firebaseId}
                      isNsfw={MediaUtils.isNsfwContent(media.explicit)}
                      isPremiumVideo={MediaUtils.isPremiumVideo(media.duration)}
                      is4k={MediaUtils.is4kVideo(media.resolution)}
                      currentUserId={currentUserId}
                      has4kAccess={false}
                      onContentLinkTap={onContentLinkTap}
                      onCommentReplyTap={onCommentReplyTap}
                      onShareTap={onShareTap}
                      onScheduleTap={onScheduleTap}
                      onDraftTap={onDraftTap}
                      onGet4kAccessTap={onGet4kAccessTap}
                      onNsfwTap={onNsfwTap}
                      onContentBlockedTap={onContentBlockedTap}
                    />
                  </div>

                  {media.description && (
                    <div className="px-4 pb-4">
                      <ExpandableRichText
                        description={media.description}
                        maxLines={2}
                        onHashtagTap={onHashtagTap}
                        onMentionTap={onMentionTap}
                        onUrlTap={onUrlTap}
                      />
                    </div>
                  )}

                  {/* Full screen mode */}
                  <div className="px-8 pt-4">
                    <ContentActions
                      contentId={media.id}
                      userId={media.firebaseId || ''}
                      subscribed={subscribed}
                      likes={media.likes}
                      comments={media.comments}
                      coins={media.coins}
                      displayAddComment={true}
                      showCoins={media.showCoins}
                      defaultGiftUrl={defaultGift ? GiftUtils.getGiftPath(defaultGift.image) : undefined}
                      isLiked={likeData?.liked || false}
                      onUserTap={onUserTap || (async () => { })}
                      onAddCommentTap={onCommentTap || (async () => { })}
                      onLikeTap={onLikeTap || (async () => { })}
                      onSendGiftTap={handleGiftTap}
                      onMoreTap={onMoreTap ? () => onMoreTap(media.firebaseId || '') : undefined}
                      onShareSocialContentTap={onShareTap || (async () => { })}
                    />
                  </div>

                </div>
              )}

              {/* Bottom overlay - actions, progress, unlock, controls */}
              <div
                className={`absolute bottom-0 left-0 right-0 z-40 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                style={{
                  background: `linear-gradient(to top, ${theme.black}CC 0%, ${theme.black}00 100%)`,
                  paddingTop: '60px',
                  paddingBottom: `calc(${responsive.safeArea.bottom} + ${responsive.spacing['2xl']}px)`,
                  paddingLeft: responsive.safeArea.left,
                  paddingRight: responsive.safeArea.right,
                  touchAction: 'auto',
                }}
                onClick={(e) => e.stopPropagation()}
              >

                {playbackState && (
                  <div className="px-4 pt-4 pb-2">
                    <div className="flex items-center justify-between px-4">
                      <div className="flex-1 flex items-center" />

                      {isVideoReady && (
                        <div
                          className="flex items-center px-3 py-1 rounded-full"
                          style={{ backgroundColor: `${theme.surface}E6` }}
                        >
                          <VideoPlayblackCurrentProgress
                            videoKey={videoKey}
                            duration={playbackState.duration}
                          />
                        </div>
                      )}

                      <div className="flex-1 flex items-center justify-end">
                        <button
                          {...qaAttr({ id: 'fullscreen-close' })}
                          onClick={handleFullscreenToggle}
                          className="p-1 rounded-full transition-colors"
                          style={{ backgroundColor: theme.transparent }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${theme.white}1A`)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = theme.transparent)}
                          aria-label="Exit fullscreen"
                        >
                          <AssetIcon
                            path="/assets/images/full-screen.png"
                            width={responsive.icon.small}
                            height={responsive.icon.small}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="px-4 py-2">
                  <VideoProgressBar
                    videoKey={videoKey}
                    duration={playbackState?.duration ?? duration}
                    typeLimit={typeLimit}
                    onSeek={seekActiveVideo}
                    isVideoReady={isInitialized}
                    onDragStart={() => {
                      setShowControls(true);
                      if (hideControlsTimeoutRef.current) clearTimeout(hideControlsTimeoutRef.current);
                    }}
                    onDragEnd={() => {
                      resetHideTimeout();
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
